import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useMotionTemplate } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, ArrowRight, Waves, ArrowUpRight,
  BarChart3, Search, Activity, Lightbulb, FileText, Zap,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EASE_OUT = [0.23, 1, 0.32, 1];

// ─── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  { title: 'AI Market Analysis',         desc: 'Map category trends, validate TAM/SAM, and surface whitespace before you commit a single sprint to building.',                          Icon: BarChart3,  accent: '#F97316' },
  { title: 'Competitor Intelligence',    desc: 'Track pricing changes, feature launches, and positioning shifts across every competing product in real time.',                          Icon: Search,     accent: '#38BDF8' },
  { title: 'Blue Ocean Strategy',        desc: 'Identify zero-competition market spaces and underserved segments using ERRC grid analysis and strategy canvas generation.',             Icon: Waves,      accent: '#2DD4BF' },
  { title: 'Multi-Source Signal Analysis', desc: 'Aggregate user feedback from Reddit, G2, App Store, HackerNews, and support tickets into structured, severity-scored insights.',   Icon: Activity,   accent: '#A78BFA' },
  { title: 'Opportunity Engine',         desc: 'Convert raw pain points into impact-ranked product opportunities with effort estimates, validation scores, and recommended next steps.', Icon: Lightbulb,  accent: '#FBBF24' },
  { title: 'AI PRD Generation',          desc: 'Auto-generate product requirement documents with user stories, acceptance criteria, and execution-ready scope from any opportunity.',   Icon: FileText,   accent: '#34D399' },
];

const MODULES = [
  { id: 'dashboard',     label: 'Dashboard',     title: 'Your product intelligence hub',        subtitle: 'Monitor signals, track pain points, and manage customer interviews-all in one place.' },
  { id: 'market',        label: 'Market',         title: 'Market intelligence, automated',        subtitle: 'Every opportunity backed by comprehensive analysis and strategic insights.' },
  { id: 'pain-points',   label: 'Pain Points',    title: 'Track every user pain point',           subtitle: 'Aggregate signals from Reddit, app stores, and support tickets with severity scoring.' },
  { id: 'opportunities', label: 'Opportunities',  title: 'Discover high-impact opportunities',    subtitle: 'AI analyzes thousands of signals to surface validated product opportunities.' },
  { id: 'blue-ocean',    label: 'Blue Ocean',     title: 'Discover untapped market spaces',       subtitle: 'Identify zero-competition opportunities where you can create entirely new demand.' },
];

// Testimonials removed per request

const LIVE_SIGNALS = [
  { source: 'Reddit',      content: '47 users report onboarding friction at step 3',       severity: 'CRITICAL', time: '2m ago',  color: '#EF4444' },
  { source: 'G2',          content: 'CSV import feature requested in 23 separate reviews', severity: 'HIGH',     time: '8m ago',  color: '#F97316' },
  { source: 'HackerNews',  content: 'Discussion thread: AI-first PM tools are winning',    severity: 'MEDIUM',   time: '14m ago', color: '#FBBF24' },
  { source: 'AppStore',    content: 'Performance lag on scroll: 12 new reports today',     severity: 'HIGH',     time: '22m ago', color: '#F97316' },
  { source: 'Support',     content: 'Slack integration missing, 156 open tickets',        severity: 'CRITICAL', time: '31m ago', color: '#EF4444' },
];

const INCOMING_SIGNALS = [
  { source: 'ProductHunt', content: 'Voted #3 Product of the Day, 847 upvotes',           severity: 'HIGH',     time: 'just now', color: '#F97316' },
  { source: 'Twitter/X',  content: 'Competitor dropped pricing by 40% today',              severity: 'CRITICAL', time: 'just now', color: '#EF4444' },
  { source: 'Capterra',   content: 'AI roadmap generator: top feature request across 40+', severity: 'HIGH',     time: 'just now', color: '#F97316' },
];

const SOURCES = [
  'Reddit', 'G2 Reviews', 'App Store', 'Google Play',
  'HackerNews', 'Capterra', 'Trustpilot', 'ProductHunt',
  'Support Tickets', 'Twitter / X',
];

const STEPS = [
  {
    n: '01',
    title: 'Connect your signal sources',
    desc: 'Link Reddit, G2 Reviews, App Store, Google Play, HackerNews, Capterra, Trustpilot, and support tickets. Infera ingests and normalizes everything automatically, no manual exports, no CSV uploads.',
    tags: ['Reddit', 'G2 Reviews', 'App Store', 'Support Tickets', 'HackerNews'],
    accent: '#F97316',
  },
  {
    n: '02',
    title: 'AI clusters and scores every signal',
    desc: 'Our AI groups feedback by product theme, scores each pain point by severity and frequency, detects emerging trends, and maps signals to your product categories, updated continuously.',
    tags: ['Semantic clustering', 'Severity scoring', 'Trend detection', 'Auto-categorization'],
    accent: '#2DD4BF',
  },
  {
    n: '03',
    title: 'Execute with validated confidence',
    desc: 'Receive impact-ranked opportunity lists, AI-generated PRDs with user stories, competitive positioning maps, and a daily brief, everything formatted for stakeholder presentations and sprint planning.',
    tags: ['Ranked opportunities', 'AI PRD generation', 'Competitor maps', 'Daily briefs'],
    accent: '#A78BFA',
  },
];

// ─── Logo ──────────────────────────────────────────────────────────────────────
const InferaLogo = ({ size = 30 }) => (
  <motion.img
    src="/infera_logo-removebg-preview.png"
    alt="Infera"
    width={size}
    height={size}
    style={{ objectFit: 'contain', flexShrink: 0, marginTop: 6 }}
    whileHover={{ scale: 1.15, rotate: -8, filter: 'drop-shadow(0 0 10px rgba(249,115,22,0.7))' }}
    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
  />
);

// ─── Background ───────────────────────────────────────────────────────────────
const Background = React.memo(() => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    <div style={{
      position: 'absolute', inset: 0, opacity: 0.02,
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
      backgroundSize: '72px 72px',
    }} />
    {/* Blobs, reduced blur + GPU layer via translateZ(0) */}
    <div className="blob-anim" style={{ position:'absolute', width:'65vw', height:'65vw', borderRadius:'50%', background:'radial-gradient(circle, #F97316, transparent 70%)', opacity:0.065, filter:'blur(72px)', top:'-25%', left:'-12%', animation:'blobFloat1 22s ease-in-out infinite', willChange:'transform', transform:'translateZ(0)' }} />
    <div className="blob-anim" style={{ position:'absolute', width:'55vw', height:'55vw', borderRadius:'50%', background:'radial-gradient(circle, #2DD4BF, transparent 70%)', opacity:0.045, filter:'blur(72px)', bottom:'-15%', right:'-12%', animation:'blobFloat2 28s ease-in-out infinite', willChange:'transform', transform:'translateZ(0)' }} />
    <div className="blob-anim" style={{ position:'absolute', width:'45vw', height:'45vw', borderRadius:'50%', background:'radial-gradient(circle, #A78BFA, transparent 70%)', opacity:0.04, filter:'blur(64px)', top:'40%', left:'30%', animation:'blobFloat3 32s ease-in-out infinite', willChange:'transform', transform:'translateZ(0)' }} />
    <div style={{ position:'absolute', inset:0, opacity:0.028, backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundSize:'180px 180px' }} />
  </div>
));

// ─── Window chrome helpers ────────────────────────────────────────────────────
const WindowChrome = React.memo(({ title, right }) => (
  <div className="h-11 border-b border-white/[0.07] flex items-center px-5 gap-3 bg-black/30 flex-shrink-0">
    <span className="text-[12px] text-white/40 font-medium">{title}</span>
    {right && <div className="ml-auto">{right}</div>}
  </div>
));

const MacFrame = React.memo(({ children }) => (
  <div className="relative">
    <div className="bg-gradient-to-b from-[#1e1e1e] to-[#111] rounded-t-2xl border-t-[2px] border-x-[2px] border-white/15 p-3">
      <div className="w-28 h-5 bg-black rounded-b-xl mx-auto mb-2" />
      {children}
    </div>
    <div className="h-3 bg-gradient-to-b from-[#252525] to-[#181818] rounded-b-2xl border-b-[2px] border-x-[2px] border-white/15" />
    <div className="h-2 bg-gradient-to-b from-[#181818] to-[#0a0a0a] rounded-b-xl mx-10" />
  </div>
));

// ─── Live Signal Feed widget ──────────────────────────────────────────────────
const MAX_SIGNALS = 4;

const LiveSignalFeed = React.memo(() => {
  const uidRef     = useRef(100);
  const incomingRef = useRef(0);
  const [signals, setSignals] = useState(() =>
    LIVE_SIGNALS.slice(0, MAX_SIGNALS).map((s, i) => ({ ...s, _uid: i }))
  );

  useEffect(() => {
    const t = setInterval(() => {
      const base = INCOMING_SIGNALS[incomingRef.current % INCOMING_SIGNALS.length];
      incomingRef.current += 1;
      const next = { ...base, _uid: uidRef.current++ };
      // Keep strictly MAX_SIGNALS items, drop the oldest
      setSignals(prev => [next, ...prev.slice(0, MAX_SIGNALS - 1)]);
    }, 3400);
    return () => clearInterval(t);
  }, []);

  const sevColor = (sev) =>
    sev === 'CRITICAL' ? { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' }
    : sev === 'HIGH'   ? { bg: 'rgba(249,115,22,0.12)', text: '#F97316' }
    :                    { bg: 'rgba(234,179,8,0.12)',  text: '#EAB308' };

  return (
    <div
      className="rounded-2xl overflow-hidden border border-white/[0.09] shadow-2xl glow-pulse w-full"
      style={{ background: 'rgba(12,11,14,0.92)', backdropFilter: 'blur(20px)' }}
    >
      {/* Header */}
      <div className="h-11 border-b border-white/[0.07] flex items-center justify-between px-4 bg-black/20">
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] text-white/38 font-medium">Signal Feed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ animation: 'signalPulse 2s infinite' }} />
          <span className="text-[10px] text-green-400 font-semibold">Live</span>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-b border-white/[0.05] bg-white/[0.02]">
        {[['847', 'signals today'], ['3', 'critical'], ['12', 'opportunities']].map(([v, l]) => (
          <div key={l} className="flex items-baseline gap-1">
            <span className="text-[13px] font-bold text-white/80">{v}</span>
            <span className="text-[10px] text-white/30">{l}</span>
          </div>
        ))}
      </div>

      {/* Signal cards, fixed height, no growth */}
      <div className="p-3 space-y-2 overflow-hidden" style={{ height: 292 }}>
        <AnimatePresence mode="sync" initial={false}>
          {signals.map((sig, i) => {
            const sc = sevColor(sig.severity);
            return (
              <motion.div
                key={sig._uid}
                initial={{ opacity: 0, y: -14, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                transition={{ duration: 0.35, ease: EASE_OUT }}
                className="p-3 rounded-xl border border-white/[0.06] hover:border-white/[0.10] transition-colors"
                style={{ background: i === 0 ? 'rgba(249,115,22,0.04)' : 'rgba(255,255,255,0.025)' }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${sig.color}18`, color: sig.color }}
                    >{sig.source}</span>
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                      style={{ backgroundColor: sc.bg, color: sc.text }}
                    >{sig.severity}</span>
                  </div>
                  <span className="text-[10px] text-white/22">{sig.time}</span>
                </div>
                <p className="text-[12px] text-white/60 leading-relaxed">{sig.content}</p>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/[0.06] bg-black/15 flex items-center justify-between">
        <span className="text-[11px] text-white/28">Aggregating 10+ sources</span>
        <button className="text-[11px] text-orange-400 hover:text-orange-300 transition-colors cursor-pointer flex items-center gap-1 font-medium">
          All signals <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
});

// ─── Source marquee ───────────────────────────────────────────────────────────
const SourceMarquee = React.memo(() => {
  const doubled = [...SOURCES, ...SOURCES];
  return (
    <div className="overflow-hidden py-6" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)' }}>
      <div className="marquee-track flex gap-8 items-center" style={{ width: 'max-content' }}>
        {doubled.map((s, i) => (
          <div
            key={i}
            className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.03] whitespace-nowrap"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-orange-400/60" />
            <span className="text-[12px] text-white/45 font-medium">{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

// ─── Dashboard Slide ──────────────────────────────────────────────────────────
const DashboardSlide = React.memo(() => (
  <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent shadow-2xl">
    <WindowChrome title="Infera, Dashboard" right={
      <span className="text-[11px] text-green-400 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 bg-green-400 rounded-full" style={{ animation:'signalPulse 2s infinite' }} />
        6 agents active
      </span>
    } />
    <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 600 }}>
      <div className="mb-6 p-4 bg-orange-500/[0.07] border border-orange-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-orange-500/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Zap className="w-4 h-4 text-orange-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] font-semibold text-orange-400 uppercase tracking-wider">Daily Brief</span>
              <span className="text-[11px] text-green-400">Updated 4m ago</span>
            </div>
            <p className="text-[13px] text-white/70 leading-relaxed">Overnight: 312 new signals ingested across Reddit, G2, HackerNews, and support tickets. 2 critical pain points surfaced. Infera identified 4 high-impact opportunities ready for PRD generation.</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Active Pain Points',  val: '214', color: 'text-orange-400' },
          { label: 'Competitors Tracked', val: '18',  color: 'text-orange-400' },
          { label: 'PRDs Generated',      val: '9',   color: 'text-purple-400' },
          { label: 'Signals Today',       val: '312', color: 'text-green-400'  },
        ].map(s => (
          <div key={s.label} className="p-4 bg-white/[0.04] border border-white/[0.07] rounded-xl hover:border-white/15 transition-colors">
            <div className="text-[10px] font-semibold text-white/35 uppercase tracking-wider mb-2">{s.label}</div>
            <div className={`text-[32px] font-bold ${s.color} mb-1`}>{s.val}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">AI Agents</span>
            <span className="text-[11px] text-green-400">6 active</span>
          </div>
          <div className="space-y-2.5">
            {[
              { name: 'Reddit Scraper',        active: true },
              { name: 'G2 Review Monitor',     active: true },
              { name: 'App Store Listener',    active: true },
              { name: 'Competitor Intel Watch',active: true },
              { name: 'Opportunity Scorer',    active: true },
              { name: 'PRD Auto-Generator',    active: true },
            ].map((a, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/[0.05] last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[12px] ${a.active ? 'text-green-400' : 'text-white/30'}`}>●</span>
                  <span className="text-[12px] text-white/60">{a.name}</span>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${a.active ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/30'}`}>{a.active ? 'Active' : 'Idle'}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4">
          <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider block mb-4">Recent Activity</span>
          <div className="space-y-3.5">
            {[
              { type: 'pain point',  desc: 'Bulk CSV signal import requested across 38 G2 reviews',  time: '6m ago',  hi: true  },
              { type: 'competitor',  desc: 'Productboard updated pricing, AI tier added',            time: '41m ago', hi: false },
              { type: 'opportunity', desc: 'Slack alert integration: 91% impact score surfaced',      time: '2h ago',  hi: true  },
            ].map((a, i) => (
              <div key={i} className="pb-3 border-b border-white/[0.05] last:border-0">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${a.hi ? 'bg-orange-500/15 text-orange-400' : 'bg-white/8 text-white/40'}`}>{a.type}</span>
                  <span className="text-[10px] text-white/30">{a.time}</span>
                </div>
                <p className="text-[12px] text-white/60 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4">
          <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider block mb-4">Scheduled Interviews</span>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] text-cyan-400 font-semibold mb-1">TODAY 2:00 PM</div>
              <div className="text-[14px] font-semibold text-white mb-0.5">Jordan Park</div>
              <div className="text-[12px] text-white/40">Senior PM · Signal Validation</div>
            </div>
            <div className="pt-3 border-t border-white/[0.07]">
              <div className="text-[10px] text-cyan-400 font-semibold mb-1">THU 10:00 AM</div>
              <div className="text-[14px] font-semibold text-white mb-0.5">Priya Nair</div>
              <div className="text-[12px] text-white/40">Head of Product · Discovery</div>
            </div>
            <button className="w-full py-2 text-[12px] text-white/50 hover:text-white border border-white/[0.07] rounded-lg hover:bg-white/5 transition-colors cursor-pointer mt-1">
              Schedule Interview
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">Top Opportunities</span>
            <span className="text-[11px] text-cyan-400">AI-ranked</span>
          </div>
          {[
            { title: 'Slack real-time signal alerts',      score: '91%', source: 'G2 + Reddit'      },
            { title: 'Auto-generated weekly PM brief',     score: '88%', source: 'User interviews'  },
            { title: 'Jira two-way PRD sync',              score: '85%', source: 'Support tickets'  },
          ].map((o, i) => (
            <div key={i} className="py-2.5 border-b border-white/[0.05] last:border-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[13px] font-medium text-white">{o.title}</span>
                <span className="text-[13px] font-semibold text-orange-400">{o.score}</span>
              </div>
              <span className="text-[11px] text-white/35">{o.source}</span>
            </div>
          ))}
        </div>
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 flex flex-col">
          <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider mb-4">Today's Focus</span>
          <div className="text-[34px] font-bold text-white mb-2">312 signals</div>
          <p className="text-[13px] text-white/50 leading-relaxed flex-1">Signals ingested from Reddit, G2, App Store, HackerNews, and support. Infera clustered them into 14 themes, 2 marked critical priority.</p>
          <button className="w-full mt-4 py-2.5 bg-white text-black text-[13px] font-semibold rounded-lg hover:bg-white/90 transition-colors cursor-pointer">View All Signals</button>
        </div>
      </div>
    </div>
  </div>
));

// ─── Market Slide ─────────────────────────────────────────────────────────────
const MarketSlide = React.memo(() => (
  <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent shadow-2xl">
    <WindowChrome title="Market Analysis" right={
      <div className="flex gap-2">
        <button className="px-3 py-1 text-[11px] text-white/50 bg-white/5 rounded-md hover:bg-white/8 transition cursor-pointer">Export</button>
        <button className="px-3 py-1 text-[11px] bg-white text-black rounded-md hover:bg-white/90 transition font-medium cursor-pointer">New analysis</button>
      </div>
    } />
    <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 600 }}>
      <div className="text-[11px] text-white/30 mb-4">AI PM Tools Market · May 2026</div>
      <div className="mb-6 p-5 bg-cyan-500/[0.06] border border-cyan-500/20 rounded-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 bg-cyan-400/15 rounded-md flex items-center justify-center">
            <FileText className="w-2.5 h-2.5 text-cyan-400" />
          </div>
          <span className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider">Executive Summary</span>
        </div>
        <p className="text-[13px] text-white/65 leading-relaxed">The PM software market is growing at 11.6% CAGR to reach $22.7B by 2034. PMs spend 52% of their time on unplanned fire-fighting instead of discovery. Infera sits at the intersection of the $22.7B PM software and $48.6B customer analytics markets, uniquely positioned to automate both.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[
          { accent: 'teal',   label: 'Market Size',      items: [['TAM', '$22.7B by 2034'], ['CAGR', '11.6% (PM software)'], ['Adjacent', '$48.6B analytics']] },
          { accent: 'orange', label: 'Key Findings',     items: [['1.', '52% of PM time goes to unplanned fire-fighting (Product Focus 2024)'], ['2.', 'PMs spend 10–15 hrs/week on manual research & competitor tracking'], ['3.', 'Only 17% of PMs spend most of their time on discovery']] },
          { accent: 'purple', label: 'Target Audience',  items: [['•', 'B2B SaaS Product Managers'], ['•', 'Startup Founders (10–500 employees)'], ['•', 'Product-led growth teams'], ['•', 'Teams tracking 500+ signals/month']] },
        ].map(col => (
          <div key={col.label} className={`p-4 bg-white/[0.04] border border-${col.accent}-500/20 rounded-xl`}>
            <div className={`flex items-center gap-2 mb-3`}>
              <div className={`w-2.5 h-2.5 bg-${col.accent}-400/40 rounded`} />
              <span className={`text-[11px] font-semibold text-${col.accent}-400 uppercase tracking-wider`}>{col.label}</span>
            </div>
            <div className="space-y-2 text-[12px]">
              {col.items.map(([k, v], i) => (
                <div key={i}><span className={`text-${col.accent}-400 font-semibold`}>{k} </span><span className="text-white/65">{v}</span></div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mb-5">
        <div className="text-[11px] font-semibold text-white/70 uppercase tracking-wider mb-3">SWOT Analysis</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Strengths',      color: 'text-green-400',  bg: 'bg-green-500/5 border-green-500/20',   items: ['AI-powered multi-source signal aggregation', 'End-to-end PM workflow, signals to PRD', 'No-code setup, live in under 10 minutes'] },
            { label: 'Weaknesses',     color: 'text-red-400',    bg: 'bg-red-500/5 border-red-500/20',       items: ['Early-stage brand vs. established tools', 'Integration breadth grows post-launch', 'Enterprise sales cycle is longer'] },
            { label: 'Opportunities',  color: 'text-blue-400',   bg: 'bg-blue-500/5 border-blue-500/20',     items: ['$22.7B PM software market, 11.6% CAGR', '+75% YoY AI tool spend surge in SaaS', 'Deep integrations with Jira, Notion, Linear'] },
            { label: 'Threats',        color: 'text-yellow-400', bg: 'bg-yellow-500/5 border-yellow-500/20', items: ['Productboard, Aha! adding AI features', 'Large incumbents may bundle similar AI', 'Data privacy & source ToS changes'] },
          ].map(s => (
            <div key={s.label} className={`p-4 border rounded-xl ${s.bg}`}>
              <div className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${s.color}`}>{s.label}</div>
              <div className="space-y-1 text-[11px] text-white/55">{s.items.map((it, i) => <div key={i}>• {it}</div>)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[['1. PM-Led PLG Motion', 'Free tier targeting individual PMs who champion Infera upward in their org'], ['2. Signal Source Breadth', 'More integrations = more value, first mover on HackerNews, Capterra, Twitter/X signals'], ['3. Community & Content', 'PM Reddit communities, Product Hunt launches, and weekly "Signal Digest" newsletter']].map(([t, d], i) => (
          <div key={i} className="p-3.5 bg-white/[0.04] border border-white/[0.07] rounded-xl">
            <div className="text-[11px] font-semibold text-white/85 mb-1.5">{t}</div>
            <div className="text-[10px] text-white/45">{d}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
));

// ─── Pain Points Slide ────────────────────────────────────────────────────────
const PainPointsSlide = React.memo(() => (
  <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent shadow-2xl">
    <WindowChrome title="Pain Points" right={
      <div className="flex gap-2">
        <button className="px-3 py-1 text-[11px] text-white/50 bg-white/5 rounded-md hover:bg-white/8 transition cursor-pointer">Export</button>
        <button className="px-3 py-1 text-[11px] bg-white text-black rounded-md hover:bg-white/90 transition font-medium cursor-pointer">Generate PRD</button>
      </div>
    } />
    <div className="bg-[#0a0b0d] overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-800/80">
        <div className="flex items-center gap-2.5 mb-2.5">
          <span className="text-white font-semibold text-[15px]">FlowDesk <span className="text-[11px] text-white/40 font-normal">(demo company — not a real company)</span></span>
          {['workflow', 'onboarding', 'api'].map(t => <span key={t} className="px-2 py-0.5 bg-gray-800 rounded-md text-[10px] text-gray-400">{t}</span>)}
        </div>
        <div className="flex items-center gap-2.5 text-[11px] text-gray-500 mb-2.5">
          <span>3 May 2026, 11:22</span><span>·</span><span>18 signals</span><span>·</span><span>4 pain points</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[['reddit • 11', '#FF6B35'], ['g2 • 4', '#3B82F6'], ['capterra • 2', '#10B981'], ['support • 1', '#F59E0B']].map(([l, c]) => (
            <span key={l} className="px-2 py-0.5 rounded-md text-[10px]" style={{ backgroundColor: `${c}15`, color: c }}>{l}</span>
          ))}
          <span className="text-[11px] text-gray-500 ml-1">● 2 critical ● 2 high</span>
        </div>
      </div>
      <div className="flex items-center gap-0 px-5 py-2.5 border-b border-gray-800/80 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <button className="px-3.5 py-1.5 text-[12px] text-cyan-400 border-b-2 border-cyan-400 font-medium cursor-pointer">Pain Points <span className="ml-1">4</span></button>
        {['Signals 18', 'Summary', 'Competitive Gaps'].map(t => <button key={t} className="px-3.5 py-1.5 text-[12px] text-gray-400 cursor-pointer">{t}</button>)}
      </div>
      <div className="flex flex-col sm:flex-row" style={{ minHeight: 'min(420px, 60vw)' }}>
        <div className="w-full sm:w-72 md:w-80 border-b sm:border-b-0 sm:border-r border-gray-800/80 p-3.5 overflow-auto flex-shrink-0">
          {[
            { sev: 'CRITICAL', score: '18/20', title: 'No bulk actions, updating 50+ tasks one-by-one kills productivity', tag: 'churn' },
            { sev: 'CRITICAL', score: '16/20', title: 'Workspace goes silent after assignment, zero status visibility',    tag: 'churn' },
            { sev: 'HIGH',     score: '13/20', title: 'API docs are incomplete, integrations break silently',              tag: ''      },
            { sev: 'HIGH',     score: '11/20', title: 'Onboarding takes hours with no guided setup for teams',              tag: ''      },
          ].map((item, i) => (
            <div key={i} className={`p-3.5 mb-2.5 rounded-xl border cursor-pointer transition-colors ${i === 0 ? 'bg-[#12141a] border-cyan-500/30' : 'bg-[#0d0e12] border-gray-800 hover:border-gray-700'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${item.sev === 'CRITICAL' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  <span className={`text-[10px] font-semibold ${item.sev === 'CRITICAL' ? 'text-red-400' : 'text-yellow-400'}`}>{item.sev}</span>
                </div>
                <span className="text-[11px] font-semibold text-gray-400">{item.score}</span>
              </div>
              <div className="text-[12px] text-white mb-1.5 leading-tight">{item.title}</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500">1 mention</span>
                {item.tag && <span className="text-[10px] text-red-400">{item.tag}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex-1 p-5 overflow-auto">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {[['CRITICAL','red'], ['Workflow & Productivity', 'gray'], ['Performance','blue'], ['Churn risk','red']].map(([l, c]) => (
              <span key={l} className={`px-2 py-0.5 bg-${c}-500/10 border border-${c}-500/20 rounded text-[11px] text-${c}-400`}>{l}</span>
            ))}
          </div>
          <h3 className="text-[18px] font-semibold text-white mb-5 leading-snug">No bulk actions, updating 50+ tasks one-by-one kills productivity</h3>
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-gray-400 uppercase tracking-wider">Severity Score</span>
              <span className="text-[17px] font-bold text-red-400">18/20</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full" style={{ width: '90%' }} />
            </div>
          </div>
          <div className="p-4 bg-[#12141a] rounded-xl border border-gray-800">
            <div className="text-[12px] font-semibold text-cyan-400 mb-2">Job: Complete repetitive task management in seconds, not hours</div>
            <p className="text-[12px] text-gray-400 leading-relaxed">Power users managing large projects report updating tasks individually as the single biggest time drain. Several users described switching to competitors specifically for bulk edit support.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
));

// ─── Opportunities Slide ──────────────────────────────────────────────────────
const OpportunitiesSlide = React.memo(() => (
  <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent shadow-2xl">
    <WindowChrome title="Opportunities" right={
      <div className="flex gap-2">
        <button className="px-3 py-1 text-[11px] text-white/50 bg-white/5 rounded-md hover:bg-white/8 transition cursor-pointer">Filter</button>
        <button className="px-3 py-1 text-[11px] bg-white text-black rounded-md hover:bg-white/90 transition font-medium cursor-pointer">Generate PRD</button>
      </div>
    } />
    <div className="p-4 sm:p-6">
        <div className="mb-5">
          <div className="text-[11px] text-purple-400 font-semibold mb-1.5 uppercase tracking-wider">High Impact Opportunity</div>
          <div className="text-[20px] font-semibold mb-1.5">Slack real-time signal alerts</div>
          <div className="text-[12px] text-white/45">94 mentions · G2, Support tickets, Reddit</div>
        </div>
        <div className="space-y-2.5 mb-6">
          {[['Impact Score', '91%', 'text-orange-400'], ['Effort', 'Low', 'text-yellow-400'], ['Validation', 'High demand', 'text-green-400']].map(([k, v, c]) => (
            <div key={k} className="flex items-center justify-between p-3 bg-white/[0.04] rounded-xl border border-white/[0.07]">
              <span className="text-[13px] text-white/70">{k}</span>
              <span className={`text-[13px] font-semibold ${c}`}>{v}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2.5">
          <button className="flex-1 py-2.5 bg-white text-black text-[13px] font-semibold rounded-xl hover:bg-white/90 transition cursor-pointer">Generate PRD</button>
          <button className="px-4 py-2.5 border border-white/[0.1] text-[13px] rounded-xl hover:bg-white/5 transition cursor-pointer">Details</button>
        </div>
      </div>
  </div>
));

// ─── Blue Ocean Slide ─────────────────────────────────────────────────────────
const BlueOceanSlide = React.memo(() => {
  const [tab, setTab] = React.useState('paths');

  const TABS = [
    { id: 'paths',  label: 'Six Paths' },
    { id: 'canvas', label: 'Strategy Canvas' },
    { id: 'errc',   label: 'ERRC Grid' },
  ];

  // ── Radar chart helpers ──
  const CX = 180, CY = 165, MAX_R = 138;
  const AXES = ['Signal Coverage', 'AI Depth', 'Discovery Speed', 'PRD Quality', 'Integrations', 'Pricing Value'];
  const N = AXES.length;
  const radarPt = (val, idx) => {
    const a = (idx * 2 * Math.PI / N) - Math.PI / 2;
    return [CX + val * MAX_R * Math.cos(a), CY + val * MAX_R * Math.sin(a)];
  };
  const toPoly = (vals) => vals.map((v, i) => radarPt(v, i).join(',')).join(' ');
  const labelPos = (idx) => {
    const a = (idx * 2 * Math.PI / N) - Math.PI / 2;
    return [CX + (MAX_R + 22) * Math.cos(a), CY + (MAX_R + 22) * Math.sin(a)];
  };

  const competitors = [
    { name: 'ProductBoard', color: '#60A5FA', values: [0.68, 0.48, 0.58, 0.55, 0.82, 0.28] },
    { name: 'Aha!',         color: '#F472B6', values: [0.52, 0.28, 0.48, 0.62, 0.68, 0.44] },
    { name: 'Dovetail',     color: '#A78BFA', values: [0.82, 0.38, 0.50, 0.40, 0.44, 0.52] },
    { name: 'Amplitude',    color: '#34D399', values: [0.62, 0.62, 0.54, 0.34, 0.72, 0.38] },
    { name: 'Infera',       color: '#F97316', values: [0.92, 0.95, 0.90, 0.93, 0.60, 0.84], highlight: true },
  ];

  const ERRC = [
    { label: 'ELIMINATE', color: 'text-red-400', border: 'border-red-500/25', bg: 'bg-red-500/5', items: [
      { title: 'Manual signal aggregation across tools', badge: 'inference', desc: 'PMs spend 15+ hrs/week manually pulling feedback from Reddit, G2, App Store, and support channels. Eliminating this unlocks discovery bandwidth that no standalone tool currently automates end-to-end.' },
      { title: 'Spreadsheet-based opportunity tracking', badge: 'inference', desc: 'Tracking signals in spreadsheets creates brittle, non-searchable knowledge that dies when PMs change roles. AI-structured opportunity records create durable institutional memory.' },
    ]},
    { label: 'REDUCE', color: 'text-yellow-400', border: 'border-yellow-500/25', bg: 'bg-yellow-500/5', items: [
      { title: 'Time from signal to validated insight', badge: 'evidenced', desc: 'Current tools require days of manual synthesis to reach a validated opportunity. Infera reduces this to under 10 minutes via automated cross-source clustering and impact scoring.' },
      { title: 'Manual competitive research overhead', badge: 'inference', desc: 'PMs dedicate 3–5 hrs/week to competitor monitoring via manual review of changelogs, reviews, and forums. Continuous automated ingestion reduces this to a passive subscription.' },
    ]},
    { label: 'RAISE', color: 'text-cyan-400', border: 'border-cyan-500/25', bg: 'bg-cyan-500/5', items: [
      { title: 'Signal coverage breadth (10+ sources)', badge: 'evidenced', desc: 'Most PM tools surface insights from 1–2 sources. Raising to 10+, Reddit, G2, App Store, Twitter, Intercom, Zendesk, GitHub issues, uncovers blind-spot opportunities competitors miss.' },
      { title: 'Opportunity validation confidence', badge: 'inference', desc: 'Pattern-matching across thousands of signals with AI scoring gives each opportunity card a validated confidence score, raising PM conviction before committing engineering resources.' },
    ]},
    { label: 'CREATE', color: 'text-green-400', border: 'border-green-500/25', bg: 'bg-green-500/5', items: [
      { title: 'Cross-source pain point clustering engine', badge: 'inference', desc: 'No existing PM tool automatically clusters semantically identical pain points across different data sources. This creates a unique evidence layer that transforms anecdote into validated pattern.' },
      { title: 'One-click PRD generation from live signals', badge: 'inference', desc: 'Generating an execution-ready PRD directly from clustered signal evidence, with user quotes, frequency data, and competitive context pre-populated, replaces hours of manual writing.' },
    ]},
  ];

  const PATHS = [
    { category: 'Buyer Groups', color: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/5',
      hypothesis: 'Target technical PMs at B2B SaaS companies currently building internal analytics tooling, who are paying engineering time to solve what Infera solves out of the box.',
      evidence: [
        { text: '23% of SaaS companies employ a dedicated "data PM" role specifically to bridge analytics tools and product decisions.', tag: 'Market intel' },
        { text: 'Engineering teams spend ~8 hrs/sprint maintaining internal signal dashboards that surface the same insights Infera generates automatically.', tag: 'Pain data' },
        { text: 'Technical PMs report highest willingness-to-pay for tools that eliminate custom scripting for feedback analysis.', tag: 'Context' },
      ],
      rec: 'Launch a "Bring Your Data Source" tier targeting technical PMs, offering API-first signal ingestion and custom connectors as the hook to displace internally-built tooling.',
    },
    { category: 'Time & Trends', color: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/5',
      hypothesis: 'AI-native product discovery will become the expected baseline within 18 months, making manual signal synthesis as outdated as manual A/B test analysis is today.',
      evidence: [
        { text: 'AI tool adoption in SaaS product teams grew 340% YoY in 2024–25, with discovery and roadmapping as the top two use cases.', tag: 'Market intel' },
        { text: 'Teams using AI-assisted discovery report 3× faster opportunity identification vs. manual methods in controlled comparisons.', tag: 'Context' },
        { text: 'G2 review volume for "AI product management" tools grew from 400 to 2,800 reviews in 12 months, signalling rapid mainstream adoption.', tag: 'Market intel' },
      ],
      rec: 'Position Infera as the intelligence layer that makes AI-native discovery the default, offering a generous free tier that establishes the habit before monetisation.',
    },
    { category: 'Alternate Industries', color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5',
      hypothesis: 'Adapt the signal aggregation model from financial intelligence platforms (Bloomberg Terminal) to product feedback, giving PMs the same always-on market pulse that traders have for equities.',
      evidence: [
        { text: 'Bloomberg Terminal users pay $24K/year purely for aggregated, structured signal feeds, the same fundamental value Infera provides for product signals.', tag: 'Context' },
        { text: 'Enterprise PM teams spend $40–80K/year on fragmented tools (Dovetail + Productboard + Mixpanel) that still leave synthesis gaps.', tag: 'Pain data' },
        { text: 'Financial intelligence consolidation reduced analyst research time by 60%, the same opportunity exists in product intelligence today.', tag: 'Market intel' },
      ],
      rec: 'Build an "Infera Feed", a real-time signal console modeled after financial terminals, where PMs monitor their product\'s signal landscape like a trader monitors a portfolio.',
    },
    { category: 'Strategic Groups', color: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/5',
      hypothesis: 'Bridge the white space between heavyweight enterprise suites (Productboard, Aha!) and lightweight survey tools, a mid-market AI layer that neither currently occupies.',
      evidence: [
        { text: 'Productboard and Aha! require 3–6 month onboarding and $15K+ annual contracts, pricing out Series A–B companies most active in discovery work.', tag: 'Pain data' },
        { text: 'Survey and interview tools (Typeform, Maze) capture feedback but offer no synthesis, leaving a processing gap PMs fill manually.', tag: 'Context' },
        { text: '67% of mid-market SaaS PMs use 4+ tools in their research workflow, citing lack of a single synthesis layer as the primary frustration.', tag: 'Market intel' },
      ],
      rec: 'Price Infera at $199–399/month for teams of 3–5 PMs to land squarely in the mid-market white space, below enterprise thresholds but above the disposable tool tier.',
    },
  ];

  return (
    <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent shadow-2xl">
      <WindowChrome title="Blue Ocean Strategy, Infera" right={
        <div className="flex items-center gap-2">
          <div className="px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] text-cyan-400 font-semibold">Score: 91/100</div>
          <button className="px-3 py-1 text-[11px] bg-cyan-500 text-white rounded-md font-medium cursor-pointer hover:bg-cyan-400 transition">Generate Report</button>
        </div>
      } />

      {/* Tab bar */}
      <div className="flex border-b border-white/[0.06] px-4 bg-black/10 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-[11px] font-medium transition border-b-2 -mb-px cursor-pointer whitespace-nowrap ${
              tab === t.id ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-white/35 hover:text-white/60'
            }`}>{t.label}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto relative" style={{ maxHeight: 530 }}>
        <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>

        {/* ── Strategy Canvas ── */}
        {tab === 'canvas' && (
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="text-[11px] text-white/30">PM Tools Market · May 2026</div>
              <div className="h-3 w-px bg-white/10" />
              <div className="text-[11px] text-white/30">6 competitive dimensions</div>
            </div>
            <div className="flex flex-col gap-4 items-center">
              <svg width="100%" height="auto" viewBox="0 0 360 330" className="mx-auto" style={{ maxWidth: 360 }}>
                {/* Grid rings */}
                {[0.25, 0.5, 0.75, 1].map(lvl => (
                  <polygon key={lvl} points={toPoly(Array(N).fill(lvl))}
                    fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                ))}
                {/* Axis spokes */}
                {AXES.map((_, i) => {
                  const [x, y] = radarPt(1, i);
                  return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />;
                })}
                {/* Axis labels */}
                {AXES.map((label, i) => {
                  const [x, y] = labelPos(i);
                  return <text key={i} x={x} y={y} fill="rgba(255,255,255,0.4)" fontSize="8.5" textAnchor="middle" dominantBaseline="middle">{label}</text>;
                })}
                {/* Competitor polygons */}
                {competitors.filter(c => !c.highlight).map(c => (
                  <polygon key={c.name} points={toPoly(c.values)}
                    fill="none" stroke={c.color} strokeWidth="1.5" strokeOpacity="0.65" />
                ))}
                {/* Infera filled polygon */}
                <polygon points={toPoly(competitors.find(c => c.highlight).values)}
                  fill="rgba(249,115,22,0.10)" stroke="#F97316" strokeWidth="2.5" />
                {competitors.find(c => c.highlight).values.map((v, i) => {
                  const [x, y] = radarPt(v, i);
                  return <circle key={i} cx={x} cy={y} r="3.5" fill="#F97316" stroke="#0a0a0a" strokeWidth="1.5" />;
                })}
              </svg>
              {/* Legend */}
              <div className="flex flex-row gap-4 flex-wrap justify-center">
                {competitors.map(c => (
                  <div key={c.name} className="flex items-center gap-1.5">
                    <div className="w-5 h-0.5 rounded shrink-0" style={{ backgroundColor: c.color }} />
                    <span className={`text-[10px] ${c.highlight ? 'text-orange-300 font-semibold' : 'text-white/40'}`}>
                      {c.name}{c.highlight ? ' ★' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[['$22.7B', 'PM software market by 2034', 'text-cyan-400'], ['+340%', 'YoY AI tool adoption in product teams', 'text-orange-400'], ['67%', 'PMs using 4+ fragmented tools today', 'text-green-400']].map(([val, sub, c]) => (
                <div key={val} className="p-3 bg-white/[0.03] border border-white/[0.07] rounded-xl">
                  <div className={`text-[22px] font-bold mb-0.5 ${c}`}>{val}</div>
                  <div className="text-[10px] text-white/35 leading-snug">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ERRC Grid ── */}
        {tab === 'errc' && (
          <div className="p-4">
            <div className="text-[11px] text-white/30 mb-3">Strategic actions across four dimensions for Infera's blue ocean positioning</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ERRC.map(section => (
                <div key={section.label} className={`p-4 border rounded-xl ${section.border} ${section.bg}`}>
                  <div className={`text-[10px] font-bold uppercase tracking-widest mb-3 ${section.color}`}>{section.label}</div>
                  <div className="space-y-3">
                    {section.items.map(item => (
                      <div key={item.title} className="pb-3 border-b border-white/[0.05] last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="text-[11.5px] font-semibold text-white/85 leading-snug">{item.title}</div>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase ${item.badge === 'evidenced' ? 'bg-green-500/15 text-green-400' : 'bg-orange-500/15 text-orange-400'}`}>{item.badge}</span>
                        </div>
                        <div className="text-[10.5px] text-white/42 leading-relaxed">{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Six Blue Ocean Paths ── */}
        {tab === 'paths' && (
          <div className="p-4">
            {/* Progress stepper */}
            <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {['Data Collection', 'Intake', 'Industry Map', 'Six Paths', 'ERRC', 'Feasibility', 'Export', 'Done'].map((step, i) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[7px] font-bold ${i <= 5 ? 'border-cyan-400 bg-cyan-400/15 text-cyan-400' : 'border-white/15 text-white/20'}`}>
                      {i <= 5 ? '✓' : ''}
                    </div>
                    <span className={`text-[8px] mt-0.5 whitespace-nowrap ${i <= 5 ? 'text-cyan-400' : 'text-white/22'}`}>{step}</span>
                  </div>
                  {i < 7 && <div className={`h-px flex-1 min-w-[8px] ${i < 5 ? 'bg-cyan-400/40' : 'bg-white/8'}`} />}
                </React.Fragment>
              ))}
            </div>
            <div className="text-[11px] text-white/30 mb-3">Six strategic lenses for discovering Infera's blue ocean opportunity space</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PATHS.map(path => (
                <div key={path.category} className={`p-4 border rounded-xl ${path.border} ${path.bg}`}>
                  <div className="flex items-center justify-between mb-2.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${path.border} ${path.color}`}>{path.category}</span>
                    <span className="text-[9px] text-white/22 border border-white/10 px-1.5 py-0.5 rounded cursor-pointer hover:text-white/50 transition">Trace</span>
                  </div>
                  <div className="text-[11px] text-white/78 leading-relaxed mb-3 font-medium">{path.hypothesis}</div>
                  <div className="mb-2.5">
                    <div className="text-[8.5px] font-bold uppercase tracking-widest text-white/22 mb-1.5">Evidence</div>
                    <div className="space-y-1.5">
                      {path.evidence.map((e, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="text-[10px] text-white/48 leading-relaxed flex-1">{e.text}</div>
                          <span className={`shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded uppercase whitespace-nowrap ${
                            e.tag === 'Market intel' ? 'bg-green-500/15 text-green-400' :
                            e.tag === 'Pain data'   ? 'bg-orange-500/15 text-orange-400' :
                                                      'bg-blue-500/15 text-blue-400'
                          }`}>{e.tag}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[8.5px] font-bold uppercase tracking-widest text-white/22 mb-1.5">Recommendation</div>
                    <div className="flex items-start gap-2">
                      <div className="text-[10px] text-white/48 leading-relaxed flex-1">{path.rec}</div>
                      <span className="shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded uppercase bg-orange-500/15 text-orange-400">inference</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
});

// ─── Slide variants ───────────────────────────────────────────────────────────
const slideVariants = {
  enter: (d) => ({ x: d > 0 ? '8%' : '-8%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:  (d) => ({ x: d < 0 ? '8%' : '-8%', opacity: 0 }),
};

// ─── Section label helper ─────────────────────────────────────────────────────
const SectionLabel = React.memo(({ children }) => (
  <div className="flex items-center justify-center gap-3 mb-5">
    <div className="h-px w-8 bg-orange-500/40" />
    <span className="text-[11px] font-semibold text-orange-400 uppercase tracking-[0.16em]">{children}</span>
    <div className="h-px w-8 bg-orange-500/40" />
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const WaitlistPage = () => {
  const [email, setEmail]         = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [moduleIndex, setModuleIndex] = useState(0);
  const [direction, setDirection]   = useState(1);

  const mainRef = useRef(null);
  const heroRef = useRef(null);

  const mouseX = useMotionValue(typeof window !== 'undefined' ? window.innerWidth / 2 : 0);
  const mouseY = useMotionValue(typeof window !== 'undefined' ? window.innerHeight / 2 : 0);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 25 });
  const springY = useSpring(mouseY, { stiffness: 40, damping: 25 });
  const spotlight = useMotionTemplate`radial-gradient(700px circle at ${springX}px ${springY}px, rgba(249,115,22,0.05), transparent 70%)`;

  const goToModule = useCallback((idx) => {
    setDirection(idx > moduleIndex ? 1 : -1);
    setModuleIndex(idx);
  }, [moduleIndex]);

  useEffect(() => {
    const fn = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    // Run hero word animation immediately; defer scroll-triggered setup until idle
    const ctx = gsap.context(() => {
      const words = heroRef.current?.querySelectorAll('.hw');
      if (words?.length) {
        gsap.from(words, { opacity: 0, y: 65, duration: 0.9, stagger: 0.065, ease: 'power3.out', delay: 0.3 });
      }
    }, mainRef);

    const scheduleScrollSetup = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const idleId = scheduleScrollSetup(() => {
      gsap.utils.toArray('.gsap-reveal').forEach(el => {
        gsap.from(el, {
          opacity: 0, y: 40, duration: 0.9, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%' },
        });
      });
      gsap.utils.toArray('.stat-num').forEach(el => {
        const target  = parseFloat(el.dataset.target);
        const isFloat = el.dataset.float === 'true';
        const suffix  = el.dataset.suffix || '';
        const prefix  = el.dataset.prefix || '';
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target, duration: 2.5, ease: 'power2.out',
          onUpdate() { el.textContent = prefix + (isFloat ? obj.val.toFixed(1) : Math.round(obj.val).toLocaleString()) + suffix; },
          scrollTrigger: { trigger: el, start: 'top 82%', once: true },
        });
      });
    });

    return () => {
      ctx.revert();
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
    };
  }, []);

  const handleMouseMove = useCallback((e) => { mouseX.set(e.clientX); mouseY.set(e.clientY); }, [mouseX, mouseY]);
  const API_URL = '/api/waitlist';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        setSubmitError(`Server error (${res.status}). Please try again.`);
        return;
      }
      if (!res.ok) {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
        return;
      }
    } catch (err) {
      console.error('[waitlist submit]', err);
      setSubmitError('Could not connect. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  return (
    <div
      ref={mainRef}
      className="min-h-screen bg-[#080709] text-white overflow-x-hidden"
      onMouseMove={handleMouseMove}
      style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}
    >
      <Background />
      <motion.div className="pointer-events-none fixed inset-0" style={{ zIndex: 1, background: spotlight }} />
      {/* Hidden but crawlable H1 for pages where the visual H1 is split across spans */}
      <h1 className="sr-only">Infera, AI Product Intelligence Platform for Product Managers</h1>


      {/* ── Header ────────────────────────────────────────────────────────── */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE_OUT }}
        className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'border-b border-white/[0.07] bg-[#080709]/80 backdrop-blur-2xl' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <InferaLogo size={75} />
            <span className="text-[22px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Infera</span>
          </div>
        </div>
      </motion.header>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section ref={heroRef} aria-label="Infera AI product intelligence platform, hero" className="relative pt-20 sm:pt-24 pb-0 px-4 sm:px-6 md:px-8 overflow-hidden" style={{ zIndex: 2 }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-20">

            {/* Left: copy */}
            <div className="flex-1 pt-6 sm:pt-10 pb-12 sm:pb-24">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: EASE_OUT }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full mb-10"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                <span className="text-[12px] text-orange-300 font-medium">AI-powered product intelligence platform · Now in early access</span>
              </motion.div>

              <h2
                className="leading-[0.93] font-extrabold mb-8"
                style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontWeight: 800,
                  letterSpacing: '-0.045em',
                  fontSize: 'clamp(48px, 5.8vw, 84px)',
                }}
              >
                {[
                  { word: 'Turn',       accent: false },
                  { word: 'scattered',  accent: false },
                  { word: 'feedback',   accent: true  },
                  { word: 'into',       accent: false },
                  { word: 'validated',  accent: false },
                  { word: 'decisions',  accent: true  },
                ].map(({ word, accent }, i) => (
                  <span key={i} className="hw inline-block mr-[0.2em]">
                    {accent ? (
                      <span style={{
                        background: 'linear-gradient(135deg, #F97316 0%, #FBBF24 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontStyle: 'italic',
                        fontWeight: 300,
                      }}>{word}</span>
                    ) : word}
                  </span>
                ))}
              </h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.85, duration: 0.8 }}
                className="text-[17px] text-white/42 mb-10 max-w-[500px] leading-relaxed"
              >
                Infera is an AI product intelligence platform that aggregates user feedback signals from Reddit, G2, App Store, and support tickets, then automatically surfaces validated opportunities and generates PRDs for product managers.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05, duration: 0.7, ease: EASE_OUT }}
                className="flex items-center gap-3 flex-wrap mb-5"
              >
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="flex gap-3 flex-wrap">
                    <input
                      id="email-input"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Enter your work email"
                      required
                      className="px-5 py-3.5 bg-white/[0.06] border border-white/[0.1] rounded-xl text-[15px] w-full sm:w-64 focus:outline-none focus:border-orange-500/40 transition-colors placeholder:text-white/22"
                    />
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      className="px-6 py-3.5 bg-orange-500 text-white text-[15px] font-semibold rounded-xl hover:bg-orange-400 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      Get early access <ArrowRight className="w-4 h-4" />
                    </motion.button>
                    {submitError && <p className="w-full text-[13px] text-red-400 mt-1">{submitError}</p>}
                  </form>
                ) : (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="px-5 py-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-[15px] font-medium"
                  >
                    ✓ You're on the list, we'll be in touch soon
                  </motion.div>
                )}
                <motion.button
                  whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.97 }}
                  onClick={() => window.open('https://drive.google.com/file/d/1ZcfwcxVMvQdkt8tu7o4lq2D8fiqrb1iW/view?usp=sharing', '_blank')}
                  className="px-6 py-3.5 border border-white/[0.1] text-[15px] font-medium rounded-xl hover:bg-white/5 transition-colors cursor-pointer text-white/60 hover:text-white"
                >
                  Watch demo
                </motion.button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="text-[12px] text-white/22"
              >
                Start free · No credit card required · Limited early access spots available
              </motion.p>
            </div>

            {/* Right: Live signal feed, desktop */}
            <motion.div
              initial={{ opacity: 0, x: 32, y: 12 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 0.6, duration: 0.9, ease: EASE_OUT }}
              className="hidden lg:block flex-shrink-0 pt-10 pb-16"
              style={{ width: 380 }}
            >
              <LiveSignalFeed />
            </motion.div>
          </div>
          {/* Signal feed, mobile/tablet, shown below the hero copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: EASE_OUT }}
            className="block lg:hidden pb-10 max-w-md mx-auto"
          >
            <LiveSignalFeed />
          </motion.div>
        </div>

        {/* Gradient fade at section bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, #080709)' }} />
      </section>

      {/* ── Source Marquee ─────────────────────────────────────────────────── */}
      <section aria-label="Signal sources, Reddit, G2, App Store, HackerNews and more" className="relative py-8 border-y border-white/[0.05]" style={{ zIndex: 2 }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-4 text-center">
          <span className="text-[11px] text-white/25 uppercase tracking-[0.14em] font-medium">Signals aggregated from</span>
        </div>
        <SourceMarquee />
      </section>

      {/* ── Features (tabbed product showcase) ────────────────────────────── */}
      <section aria-label="Infera features, dashboard, market intelligence, pain point tracking, opportunity discovery, blue ocean strategy" className="py-16 sm:py-24 px-4 sm:px-6 md:px-8 border-b border-white/[0.05]" style={{ zIndex: 2, position: 'relative' }}>
        <div className="max-w-[1200px] mx-auto">

          {/* Heading */}
          <div className="gsap-reveal text-center mb-12">
            <SectionLabel>Features</SectionLabel>
            <h2
              className="text-[clamp(34px,4vw,56px)] font-bold leading-[1.06] mb-5"
              style={{ fontFamily: "'Bricolage Grotesque', sans-serif", letterSpacing: '-0.02em' }}
            >
              Explore the core capabilities
              <br />
              <span className="text-white/28 font-light italic">that make the platform powerful</span>
            </h2>
            <p className="text-[16px] leading-relaxed text-white/36 max-w-[480px] mx-auto">
              A complete AI product intelligence system, from raw signal ingestion to validated opportunities and execution-ready PRDs.
            </p>
          </div>

          {/* Underline tabs, matching the reference style */}
          <div className="flex items-center justify-start sm:justify-center border-b border-white/[0.08] mb-0 gap-0 overflow-x-auto scrollbar-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {MODULES.map((m, i) => {
              const icons = [BarChart3, BarChart3, Activity, Lightbulb, Waves];
              const Icon  = icons[i] || BarChart3;
              const active = i === moduleIndex;
              return (
                <button
                  key={m.id}
                  onClick={() => goToModule(i)}
                  className={`relative flex items-center gap-2 px-5 py-3.5 text-[13px] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    active ? 'text-white' : 'text-white/38 hover:text-white/65'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-orange-400' : 'text-white/25'}`} />
                  {m.label}
                  {active && (
                    <motion.div
                      layoutId="tab-underline"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-orange-500 rounded-t-full"
                      transition={{ duration: 0.25, ease: EASE_OUT }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Content panel */}
          <div
            className="rounded-b-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderTop: 'none',
              boxShadow: '0 40px 100px rgba(0,0,0,0.45)',
            }}
          >
            {/* Module subtitle bar */}
            <div className="px-4 sm:px-8 py-4 sm:py-5 border-b border-white/[0.05] flex items-center justify-between gap-4 sm:gap-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`meta-${moduleIndex}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: EASE_OUT }}
                >
                  <div className="text-[15px] font-semibold text-white mb-0.5" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                    {MODULES[moduleIndex].title}
                  </div>
                  <div className="text-[13px] text-white/38">{MODULES[moduleIndex].subtitle}</div>
                </motion.div>
              </AnimatePresence>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => goToModule(Math.max(0, moduleIndex - 1))}
                  disabled={moduleIndex === 0}
                  className="w-8 h-8 flex items-center justify-center border border-white/[0.09] rounded-lg hover:bg-white/5 transition disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => goToModule(Math.min(MODULES.length - 1, moduleIndex + 1))}
                  disabled={moduleIndex === MODULES.length - 1}
                  className="w-8 h-8 flex items-center justify-center border border-white/[0.09] rounded-lg hover:bg-white/5 transition disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Slide */}
            <div className="relative overflow-hidden" style={{ minHeight: 'auto' }}>
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={moduleIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.38, ease: EASE_OUT }}
                >
                  {moduleIndex === 0 && <DashboardSlide />}
                  {moduleIndex === 1 && <MarketSlide />}
                  {moduleIndex === 2 && <PainPointsSlide />}
                  {moduleIndex === 3 && <OpportunitiesSlide />}
                  {moduleIndex === 4 && <BlueOceanSlide />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section aria-label="How Infera works, connect sources, AI analysis, execute with confidence" className="px-4 sm:px-6 md:px-8 py-16 sm:py-28 border-b border-white/[0.05]" style={{ zIndex: 2, position: 'relative' }}>
        <div className="max-w-[1100px] mx-auto">
          <div className="gsap-reveal text-center mb-16">
            <SectionLabel>How it works</SectionLabel>
            <h2
              className="text-[clamp(32px,3.8vw,50px)] font-bold leading-[1.08] mb-4"
              style={{ fontFamily: "'Bricolage Grotesque', sans-serif", letterSpacing: '-0.018em' }}
            >
              From raw feedback to product roadmap
              <br />
              <span className="text-white/28 font-light italic">in three automated steps</span>
            </h2>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Connector line, desktop only */}
            <div className="hidden md:block absolute top-14 left-1/3 right-1/3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" style={{ marginLeft: '8%', marginRight: '8%' }} />

            {STEPS.map((step, i) => (
              <motion.div
                key={step.n}
                className="gsap-reveal relative p-7 rounded-2xl border border-white/[0.07] bg-white/[0.025] hover:border-white/[0.12] transition-all duration-300"
              >
                {/* Step number */}
                <div
                  className="text-[12px] font-bold mb-5 tabular-nums"
                  style={{ color: step.accent, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                >
                  {step.n}
                </div>
                <h3 className="text-[17px] font-semibold text-white mb-3" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-[13px] text-white/42 leading-relaxed mb-5">{step.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {step.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-[11px] px-2.5 py-1 rounded-full border font-medium"
                      style={{ backgroundColor: `${step.accent}0e`, borderColor: `${step.accent}22`, color: step.accent }}
                    >{tag}</span>
                  ))}
                </div>
                {/* Connector arrow, between cards */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:flex absolute -right-4 top-14 items-center justify-center w-8 h-8 z-10">
                    <ArrowRight className="w-4 h-4 text-white/15" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <section aria-label="Infera by the numbers, signals tracked, product teams, market growth" className="py-16 sm:py-24 px-4 sm:px-6 md:px-8 border-b border-white/[0.05]" style={{ zIndex: 2, position: 'relative' }}>
        <div className="max-w-[1300px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { label: 'of PM time lost to fire-fighting, not strategy',   target: 52,   suffix: '%', source: 'Product Focus / UXcam 2024' },
              { label: 'hrs/week on manual research & competitor tracking', target: 15,   suffix: '',  source: 'Chisel Labs / Atlassian 2025' },
              { label: 'of PMs spend most of their time on discovery',     target: 17,   suffix: '%', source: 'ProductPlan 2024' },
              { label: 'PM software market size by 2034',                  target: 22.7, suffix: 'B', float: true, prefix: '$', source: 'DataIntelo 2025' },
            ].map((s) => (
              <div key={s.label} className="gsap-reveal">
                <div
                  className="stat-num text-[40px] sm:text-[54px] font-extrabold text-orange-400 leading-none mb-2"
                  data-target={s.target}
                  data-suffix={s.suffix}
                  data-float={s.float ? 'true' : 'false'}
                  data-prefix={s.prefix || ''}
                  style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
                >
                  {s.prefix || ''}0{s.suffix}
                </div>
                <div className="text-[13px] text-white/55 font-medium leading-snug mb-1">{s.label}</div>
                <div className="text-[11px] text-white/22 italic">{s.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials section removed */}

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section aria-label="Join the Infera waitlist, early access to AI product intelligence" className="px-4 sm:px-6 md:px-8 py-20 sm:py-32 border-b border-white/[0.05]" style={{ zIndex: 2, position: 'relative' }}>
        <div className="max-w-[1200px] mx-auto">
          <div className="gsap-reveal">
            <SectionLabel>Early access · Limited beta seats</SectionLabel>
            <h2
              className="mb-6 font-extrabold leading-[0.96] tracking-tight"
              style={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: '-0.045em',
                fontSize: 'clamp(40px, 5.5vw, 78px)',
              }}
            >
              Stop guessing.
              <br />
              <span className="text-white/26 font-light italic">Start shipping what</span>
              <br />
              users actually need.
            </h2>
            <p className="mb-12 max-w-[520px] text-[17px] leading-relaxed text-white/36">
              Join the waitlist for early access to the AI product intelligence platform built for product managers who want to ship smarter. Get in early and directly shape our product roadmap.
            </p>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex w-full max-w-[600px] flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your work email"
                  required
                  className="px-5 py-3.5 bg-white/[0.06] border border-white/[0.1] rounded-xl text-[15px] w-full sm:flex-1 focus:outline-none focus:border-orange-500/40 transition-colors placeholder:text-white/22"
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  className="w-full sm:w-auto px-6 py-3.5 bg-orange-500 text-white text-[15px] font-semibold rounded-xl hover:bg-orange-400 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Join waitlist <ArrowRight className="w-4 h-4" />
                </motion.button>
                {submitError && <p className="w-full text-[13px] text-red-400 mt-1">{submitError}</p>}
              </form>
            ) : (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-6 py-4 text-[15px] text-emerald-400 font-medium"
              >
                You're on the list. We'll be in touch soon.
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer style={{ zIndex: 2, position: 'relative' }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <InferaLogo size={30} />
            <span className="text-[13px] text-white/28" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              © 2026 Infera
            </span>
          </div>
          <div className="flex items-center gap-6">
            {[
              { label: 'Twitter', href: '#' },
              { label: 'LinkedIn', href: 'https://www.linkedin.com/company/inferaai/about/?viewAsMember=true' },
              { label: 'Contact', href: 'mailto:workspace@inferaai.dev' }
            ].map(link => (
              <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="text-[13px] text-white/28 hover:text-white/65 transition-colors cursor-pointer">{link.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistPage;
